import * as React from "react";
import Image from "next/image";
import Footer from "@/components/Footer";
import Header from "@/components/Header";
import { getOne } from "@/api/contentful";
import { Providers } from "@/types/providers";
import PageSEO from "@/components/Shared/PageSEO";
import WhyChooseUs from "@/components/WhyChooseUs";
import { HeaderImage as HeaderImageType } from "@/types/header";
import InsuranceCards from "@/components/Insurance/InsuranceCardList";
import InsuranceBenefits from "@/components/Insurance/InsuranceBenefits";
import WeWorkWithInsurance from "@/components/Insurance/WeWorkWithInsurance";
import InsuranceHeaderContent from "@/components/Header/InsuranceHeaderContent";
import { CAR_INSURANCE_HEADER_IMAGE, INSURANCE_PROVIDERS } from "@/data/consts";

const Breadcrumbs = [
	{
		title: "Home",
		href: "/",
		active: false,
	},
	{
		title: "Insurance",
		href: "/insurance",
		active: true,
	},
];

const headerContentListItems = [
	"Compare 110+ providers",
	"See if you can find a better deal",
	"Choose type of insurance you require",
];

const insuranceBenefitsListItems = [
	{
		heading: "Time",
		text: "Quickly and easily compare insurance in one place",
	},
	{
		heading: "200+",
		text: "Get access to over 200 insurance providers.",
	},
	{ heading: "60+", text: "Compare over 60 insurance products" },
	{
		heading: "Free",
		text: "The comparison site is completely free to use.",
	},
];

const Insurance: React.FC<{
	headerImage: HeaderImageType;
	providers: {
		fields: {
			providerLogos: Array<Providers>;
		};
	};
}> = ({ headerImage, providers }) => {
	return (
		<div className="h-100 d-flex flex-column">
			<PageSEO
				title={
					"Compare Affordable Home, Car, Van Insurance and More | Consumer Choices"
				}
				description={
					"Compare car, home, van, bicycle, travel & more insurance options easily with Consumer Choices. Find the best rates & coverage from top providers in minutes. Save time & money today."
				}
				keywords={"Insurance"}
			/>
			<Header
				left={
					<InsuranceHeaderContent
						title={"Compare Insurance Quotes"}
						listItems={headerContentListItems}
						landing={true}
					/>
				}
				right={
					<Image
						src={"https:" + headerImage.fields.image?.fields.file?.url}
						alt={headerImage.fields.image?.fields.title ?? ""}
						height="350"
						width="600"
					/>
				}
				theme={{
					paddingTop: 5,
					paddingRight: 0,
					paddingLeft: 0,
					paddingBottom: 0,
				}}
				breadcrumbs={Breadcrumbs}
			/>
			<InsuranceCards
				title={"Compare insurance quotes from top UK insurance providers"}
			/>
			<WeWorkWithInsurance providers={providers.fields.providerLogos} />
			<InsuranceBenefits
				headingTitle={"Looking for insurance?"}
				benefits={insuranceBenefitsListItems}
			/>
			<WhyChooseUs />
			<section className={` pt-5 pb-5 text-center bg-blue`}>
				<div className="container-fluid pt-5 pb-5">
					<InsuranceHeaderContent
						title={"Compare Insurance Quotes"}
						listItems={headerContentListItems}
						landing={true}
						bottomHeader={true}
					/>
				</div>
			</section>
			<Footer />
		</div>
	);
};

export async function getStaticProps() {
	const providers = await getOne(INSURANCE_PROVIDERS);
	const headerImage = await getOne(CAR_INSURANCE_HEADER_IMAGE);

	return {
		props: {
			headerImage,
			providers,
		},
		revalidate: Number(process.env.REVALIDATE_CONTENTFUL),
	};
}

export default Insurance;
